import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { Button } from "@/components/ui/button";
import {
  MessageSquareText,
  ArrowRight,
  AlertCircle,
  Calendar,
  MapPin,
  Bot,
  ListChecks,
  Mic,
  CalendarDays,
  CalendarClock,
  History as HistoryIcon,
  Sparkles,
  Flag,
  User,
  Users,
  Wand2,
  CheckCircle2,
  Circle,
  Megaphone,
  ClipboardList,
  Database,
  Wallet,
  Lock,
} from "lucide-react";

const PLATFORM_LABEL = { facebook: "Facebook", x: "X", instagram: "Instagram" };

function TrialBanner() {
  const billing = useBilling();
  if (!billing.loaded) return null;
  if (billing.is_owner || billing.is_comp) return null;
  if (billing.status === "pro" || billing.status === "standard") return null;
  if (billing.status === "trial") {
    const days = billing.trial_days_left ?? 0;
    const urgent = days <= 2;
    return (
      <div
        className={`rounded-xl border px-5 py-4 flex flex-wrap items-center justify-between gap-3 ${
          urgent ? "bg-flag-red/[0.06] border-flag-red/40" : "bg-amber-50 border-amber-200"
        }`}
        data-testid="trial-banner"
      >
        <div className="flex items-start gap-3">
          <Sparkles className={`h-5 w-5 mt-0.5 ${urgent ? "text-flag-red" : "text-amber-700"}`} />
          <div>
            <div className={`font-display font-bold ${urgent ? "text-flag-navy" : "text-amber-900"}`}>
              {days <= 0
                ? "Your trial ends today."
                : `Free trial — ${days} day${days === 1 ? "" : "s"} left.`}
            </div>
            <div className={`text-sm ${urgent ? "text-slate-700" : "text-amber-900/80"}`}>
              Full Pro access through your trial. No card on file — pick a plan to keep going.
            </div>
          </div>
        </div>
        <Link to="/billing" data-testid="trial-banner-cta">
          <Button className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-10 px-5">
            Choose a plan
          </Button>
        </Link>
      </div>
    );
  }
  if (billing.status === "expired") {
    return (
      <div className="rounded-xl border bg-flag-red/[0.06] border-flag-red/40 px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="trial-banner-expired">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 mt-0.5 text-flag-red" />
          <div>
            <div className="font-display font-bold text-flag-navy">Your trial has ended.</div>
            <div className="text-sm text-slate-700">Pick a plan to unlock your campaign tools again.</div>
          </div>
        </div>
        <Link to="/billing" data-testid="trial-banner-expired-cta">
          <Button className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-10 px-5">
            Choose a plan
          </Button>
        </Link>
      </div>
    );
  }
  return null;
}

const SOTR_KEYS = [
  "race_status", "candidate_experience", "partisan_lean", "recent_election_results",
  "contested_primary", "endorsements", "budget_fundraising", "organization",
  "already_built", "district_profile", "core_message", "strengths",
  "background_story", "communication_style", "aggressiveness", "time_commitment",
  "strengths_vs_needs", "other_context",
];

function computeSetupSteps(c) {
  if (!c) return [];
  const isCommittee = c.account_type === "committee";
  const primaryName = isCommittee ? c.committee_name : c.candidate_name;
  const primaryOffice = isCommittee ? c.jurisdiction || c.office_sought : c.office_sought;
  const issues = (c.top_issues || []).filter((x) => (x || "").trim());
  const sotrCount = SOTR_KEYS.filter((k) => (c[k] || "").trim()).length;
  const opponentDone = isCommittee
    ? !!(c.opposition_messaging || "").trim()
    : !!(c.opponent_name || "").trim();
  const bioDone = !!(c.bio || c.background_story || "").trim();
  return [
    { id: "basics", label: "Race basics — name, office, district", done: !!(primaryName && primaryOffice && c.district), to: "/intake?step=1" },
    { id: "election_date", label: "Election date", done: !!(c.election_date || "").trim(), to: "/intake?step=1" },
    { id: "issues", label: "Top issues", done: issues.length >= 1, to: "/intake?step=2" },
    { id: "opponent", label: isCommittee ? "Opposing party messaging" : "The opponent", done: opponentDone, to: "/intake?step=3" },
    { id: "bio", label: "Bio / background story", done: bioDone, to: "/intake?step=2" },
    { id: "sotr", label: "State of the Race (3+ details)", done: sotrCount >= 3, to: "/intake?step=5" },
  ];
}

function SetupProgressBar({ campaign }) {
  const steps = useMemo(() => computeSetupSteps(campaign), [campaign]);
  if (!campaign || steps.length === 0) return null;
  const done = steps.filter((s) => s.done).length;
  const total = steps.length;
  const pct = Math.round((done / total) * 100);
  if (pct >= 100) return null; // hide at 100% — no nagging fully set-up users
  return (
    <div className="bg-white border-2 border-flag-red/30 rounded-2xl p-5 sm:p-6 shadow-sm" data-testid="setup-progress-bar">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-flag-red" />
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-flag-red">Account setup</p>
            <span data-testid="setup-progress-pct" className="text-xs font-bold text-slate-700">
              {done} of {total} · {pct}%
            </span>
          </div>
          <h2 className="font-display text-xl font-bold text-flag-navy mt-1">Complete your setup to get the most out of Electable.</h2>
        </div>
        <Link
          to="/intake"
          data-testid="setup-import-cta"
          className="inline-flex items-center gap-2 bg-flag-red text-white font-bold px-4 h-10 rounded-md hover:bg-flag-red-hover transition-colors"
        >
          <Wand2 className="h-4 w-4" />
          Import from another AI
          <span className="text-[9px] font-bold uppercase tracking-wider bg-white/20 px-1.5 py-0.5 rounded">Recommended</span>
        </Link>
      </div>
      <div className="mt-4 h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-testid="setup-progress-fill">
        <div className="h-full bg-flag-red transition-all" style={{ width: `${pct}%` }} />
      </div>
      <ul className="mt-4 grid sm:grid-cols-2 gap-2" data-testid="setup-progress-steps">
        {steps.map((s) => (
          <li key={s.id}>
            <Link
              to={s.to}
              data-testid={`setup-step-${s.id}`}
              className={`flex items-center gap-2 text-sm py-2 px-3 rounded-md transition-colors ${s.done ? "text-slate-500" : "text-slate-800 hover:bg-slate-50"}`}
            >
              {s.done ? (
                <CheckCircle2 className="h-4 w-4 text-flag-red flex-shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-slate-300 flex-shrink-0" />
              )}
              <span className={s.done ? "line-through" : "font-medium"}>{s.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function daysUntil(yyyyMmDd) {
  if (!yyyyMmDd) return null;
  try {
    const target = new Date(`${yyyyMmDd}T00:00:00`);
    if (Number.isNaN(target.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((target - today) / 86400000);
    return diff;
  } catch {
    return null;
  }
}

function formatEventDate(yyyyMmDd) {
  try {
    return new Date(`${yyyyMmDd}T00:00:00`).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return yyyyMmDd;
  }
}

function formatRelativeTime(iso) {
  try {
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function HeroHeader({ campaign, isCommittee, daysLeft }) {
  const displayName = isCommittee ? campaign?.committee_name : campaign?.candidate_name;
  const subline = isCommittee
    ? `${campaign?.party || ""}${campaign?.jurisdiction ? ` · ${campaign.jurisdiction}` : ""}`.trim()
    : `${campaign?.office_sought || ""}${campaign?.district ? ` · ${campaign.district}` : ""}`.trim();

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-flag-navy/15 bg-flag-navy text-white shadow-md"
      data-testid="dashboard-hero"
    >
      {/* decorative stripes */}
      <div className="absolute inset-y-0 right-0 w-1/2 opacity-[0.07] pointer-events-none bg-[repeating-linear-gradient(135deg,white_0_2px,transparent_2px_14px)]" />
      <div className="absolute -top-12 -right-10 h-48 w-48 rounded-full bg-flag-red/20 blur-3xl pointer-events-none" />

      <div className="relative px-6 sm:px-10 py-8 sm:py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-flag-red text-white">
              <Flag className="h-3 w-3" />
            </span>
            {isCommittee ? "Committee HQ" : "Campaign HQ"}
          </div>
          <h1
            data-testid="hero-wordmark"
            className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight tracking-tight"
          >
            {displayName || (isCommittee ? "Your Committee" : "Your Campaign")}
          </h1>
          {subline && (
            <p data-testid="hero-office" className="mt-2 text-base sm:text-lg text-white/85 flex items-center gap-2">
              <MapPin className="h-4 w-4 opacity-80" />
              {subline}
            </p>
          )}
        </div>

        {!isCommittee && (
          <div
            className="flex-shrink-0 bg-flag-red text-white rounded-xl px-5 py-4 sm:px-6 sm:py-5 shadow-lg shadow-flag-red/20 border border-white/15"
            data-testid="hero-countdown"
          >
            {daysLeft === null || daysLeft === undefined ? (
              <Link to="/intake" className="block">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-90">Election Day</div>
                <div className="font-display text-2xl font-black leading-none mt-1">Set the date</div>
                <div className="mt-2 text-xs font-semibold opacity-95 inline-flex items-center gap-1">
                  Add election date <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            ) : daysLeft > 0 ? (
              <>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-90">Election Day in</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span data-testid="hero-countdown-days" className="font-display text-5xl sm:text-6xl font-black leading-none tabular-nums">
                    {daysLeft}
                  </span>
                  <span className="font-semibold text-sm opacity-90">days</span>
                </div>
                <div className="mt-1 text-xs font-medium opacity-90">{formatEventDate(campaign?.election_date)}</div>
              </>
            ) : daysLeft === 0 ? (
              <>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-90">It&apos;s Election Day</div>
                <div className="font-display text-3xl font-black leading-none mt-1">Today.</div>
                <div className="mt-1 text-xs font-semibold opacity-95">Get out the vote.</div>
              </>
            ) : (
              <>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-90">Election was</div>
                <div className="font-display text-3xl font-black leading-none mt-1">{Math.abs(daysLeft)}d ago</div>
                <div className="mt-1 text-xs font-medium opacity-90">{formatEventDate(campaign?.election_date)}</div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PriorityCard({ to, testid, icon: Icon, badge, title, blurb, cta, tone = "red" }) {
  const toneCls =
    tone === "red"
      ? {
          border: "border-flag-red/20 hover:border-flag-red",
          iconBg: "bg-flag-red text-white",
          badge: "text-flag-red bg-flag-red/10",
          cta: "text-flag-red",
        }
      : {
          border: "border-flag-navy/20 hover:border-flag-navy",
          iconBg: "bg-flag-navy text-white",
          badge: "text-flag-navy bg-flag-navy/10",
          cta: "text-flag-navy",
        };

  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group relative overflow-hidden bg-white border-2 ${toneCls.border} rounded-2xl p-6 sm:p-7 shadow-sm hover:shadow-md transition-all`}
    >
      <div className="flex items-start gap-4">
        <div className={`h-14 w-14 flex-shrink-0 rounded-xl ${toneCls.iconBg} flex items-center justify-center shadow-sm`}>
          <Icon className="h-7 w-7" />
        </div>
        <div className="min-w-0">
          <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.16em] px-2 py-0.5 rounded-full ${toneCls.badge}`}>
            {badge}
          </span>
          <h3 className="mt-2 font-display text-xl sm:text-2xl font-bold text-flag-navy">{title}</h3>
          <p className="mt-1.5 text-slate-600 leading-relaxed">{blurb}</p>
          <div className={`mt-4 inline-flex items-center gap-1 font-bold ${toneCls.cta}`}>
            {cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function ToolCard({ to, testid, icon: Icon, title, blurb, cta, iconTone = "navy" }) {
  const iconCls = iconTone === "red" ? "bg-flag-red/10 text-flag-red" : "bg-flag-navy/10 text-flag-navy";
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group bg-white border border-slate-200 rounded-xl p-5 sm:p-6 shadow-sm hover:border-flag-navy hover:shadow-md transition-all"
    >
      <div className={`h-11 w-11 rounded-lg ${iconCls} flex items-center justify-center`}>
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 font-display text-lg font-bold text-flag-navy">{title}</h3>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{blurb}</p>
      <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-flag-red">
        {cta} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  );
}

function SnapshotChecklistCard({ data }) {
  const completed = data?.completed_count ?? 0;
  const total = data?.total_items ?? 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <Link
      to="/launch-checklist"
      data-testid="snapshot-launch-checklist"
      className="group bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:border-flag-navy hover:shadow-md transition-all flex flex-col"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-flag-navy/10 text-flag-navy">
          <ListChecks className="h-4 w-4" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Launch Checklist</span>
      </div>
      <div className="mt-3 font-display text-2xl font-black text-flag-navy tabular-nums" data-testid="snapshot-launch-progress-text">
        {completed} <span className="text-slate-400 font-bold text-lg">of</span> {total}
      </div>
      <div className="text-sm text-slate-600 mt-0.5">steps done</div>
      <div className="mt-3 h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-testid="snapshot-launch-progress-bar">
        <div
          className="h-full bg-flag-red transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-auto pt-3 inline-flex items-center gap-1 text-sm font-semibold text-flag-red">
        Continue <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  );
}

function SnapshotEventCard({ event }) {
  return (
    <Link
      to="/events"
      data-testid="snapshot-next-event"
      className="group bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:border-flag-navy hover:shadow-md transition-all flex flex-col"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-flag-navy/10 text-flag-navy">
          <CalendarClock className="h-4 w-4" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Next event</span>
      </div>
      {event ? (
        <>
          <div className="mt-3 font-display text-lg font-bold text-flag-navy leading-snug line-clamp-2" data-testid="snapshot-next-event-title">
            {event.title}
          </div>
          <div className="mt-1 text-sm text-slate-700 inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-slate-500" /> {formatEventDate(event.event_date)}
            {event.event_time && <span className="text-slate-500">· {event.event_time}</span>}
          </div>
          {event.location && (
            <div className="text-sm text-slate-500 mt-0.5 truncate">{event.location}</div>
          )}
        </>
      ) : (
        <>
          <div className="mt-3 font-display text-lg font-bold text-flag-navy leading-snug">Nothing on the calendar</div>
          <div className="mt-1 text-sm text-slate-600">Add your first campaign event.</div>
        </>
      )}
      <div className="mt-auto pt-3 inline-flex items-center gap-1 text-sm font-semibold text-flag-red">
        {event ? "Open calendar" : "Add event"} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  );
}

function SnapshotDraftCard({ draft }) {
  const firstDraft = draft?.drafts?.[0];
  const preview = firstDraft?.text || draft?.moment || "";
  const platform = firstDraft?.platform ? PLATFORM_LABEL[firstDraft.platform] : null;

  return (
    <Link
      to="/history"
      data-testid="snapshot-recent-draft"
      className="group bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:border-flag-navy hover:shadow-md transition-all flex flex-col"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-flag-red/10 text-flag-red">
          <HistoryIcon className="h-4 w-4" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Latest draft</span>
      </div>
      {draft ? (
        <>
          <div className="mt-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            {platform && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{platform}</span>}
            <span>{formatRelativeTime(draft.created_at)}</span>
          </div>
          <p
            className="mt-2 text-sm text-slate-800 leading-snug line-clamp-3"
            data-testid="snapshot-recent-draft-preview"
          >
            {preview}
          </p>
        </>
      ) : (
        <>
          <div className="mt-3 font-display text-lg font-bold text-flag-navy leading-snug">No drafts yet</div>
          <div className="mt-1 text-sm text-slate-600">Generate your first post.</div>
        </>
      )}
      <div className="mt-auto pt-3 inline-flex items-center gap-1 text-sm font-semibold text-flag-red">
        {draft ? "Open history" : "Open generator"} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  );
}

function ProfileSummary({ campaign, isCommittee }) {
  const name = isCommittee ? campaign?.committee_name : campaign?.candidate_name;
  const meta = isCommittee
    ? campaign?.jurisdiction || campaign?.party || ""
    : campaign?.office_sought || "";
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-5 sm:px-6 shadow-sm flex flex-wrap items-center justify-between gap-4"
      data-testid="dashboard-profile-summary"
    >
      <div className="flex items-center gap-4 min-w-0">
        <div className="h-11 w-11 rounded-lg bg-flag-navy/5 text-flag-navy flex items-center justify-center flex-shrink-0">
          {isCommittee ? <Users className="h-5 w-5" /> : <User className="h-5 w-5" />}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
            {isCommittee ? "Committee profile" : "Campaign profile"}
          </div>
          <div className="font-display text-base font-bold text-flag-navy truncate" data-testid="profile-summary-name">
            {name || (isCommittee ? "Your committee" : "Your campaign")}
          </div>
          {meta && <div className="text-sm text-slate-600 truncate">{meta}</div>}
        </div>
      </div>
      <Link
        to="/settings"
        data-testid="profile-view-full-link"
        className="inline-flex items-center gap-1 text-sm font-semibold text-flag-navy hover:underline"
      >
        View full profile <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState(null);
  const [events, setEvents] = useState([]);
  const [recentDraft, setRecentDraft] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/campaign")
      .then(({ data }) => {
        if (!cancelled) setCampaign(data || null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isCommittee = campaign?.account_type === "committee";
  const primaryName = isCommittee ? campaign?.committee_name : campaign?.candidate_name;
  // Minimal threshold: a saved campaign with at least a name. Dashboard renders for partial profiles;
  // the SetupProgressBar guides the user to complete the rest. We never gate access to tools.
  const isReady = !!campaign && !!primaryName;

  // Snapshot data fetches — only after we know the account type / readiness
  useEffect(() => {
    if (!isReady) return;
    let cancelled = false;
    if (!isCommittee) {
      api
        .get("/launch-checklist")
        .then(({ data }) => {
          if (!cancelled) setChecklist(data);
        })
        .catch(() => {
          if (!cancelled) setChecklist(null);
        });
    }
    api
      .get("/campaign-events")
      .then(({ data }) => {
        if (cancelled) return;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcoming = (data || [])
          .filter((e) => {
            try {
              return new Date(`${e.event_date}T00:00:00`) >= today;
            } catch {
              return false;
            }
          })
          .sort((a, b) => a.event_date.localeCompare(b.event_date));
        setEvents(upcoming);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });
    api
      .get("/posts/history")
      .then(({ data }) => {
        if (cancelled) return;
        setRecentDraft((data || [])[0] || null);
      })
      .catch(() => {
        if (!cancelled) setRecentDraft(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isReady, isCommittee]);

  const daysLeft = useMemo(() => daysUntil(campaign?.election_date), [campaign?.election_date]);
  const nextEvent = events?.[0] || null;

  // ===== Loading shell =====
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="h-40 bg-slate-100 rounded-2xl animate-pulse" />
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div className="h-32 bg-slate-100 rounded-xl animate-pulse" />
          <div className="h-32 bg-slate-100 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  // ===== Empty-profile state =====
  if (!isReady) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Dashboard</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">
          Welcome, {user?.name?.split(" ")[0] || "Candidate"}.
        </h1>
        <div
          className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between bg-white border border-slate-200 rounded-xl p-6 sm:p-8 shadow-sm"
          data-testid="dashboard-empty-profile"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-flag-red mt-1" />
            <div>
              <div className="font-display text-xl font-bold text-flag-navy">Let&apos;s set up your profile</div>
              <p className="text-slate-600 mt-1">
                Tell us whether this is a candidate or a party committee, and we&apos;ll tailor the intake — every draft sounds right after that.
              </p>
            </div>
          </div>
          <Link to="/intake" data-testid="dashboard-start-intake-btn">
            <Button className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-12 px-6">
              Start intake <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // ===== Ready state =====
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 space-y-8">
      <TrialBanner />

      {/* SETUP PROGRESS — hides automatically at 100% */}
      <SetupProgressBar campaign={campaign} />

      {/* HERO */}
      <HeroHeader campaign={campaign} isCommittee={isCommittee} daysLeft={daysLeft} />

      {/* CATEGORY HUB — the new card-based home for non-technical users.
          Six rectangles, one per category, each leading to its tools (or
          directly to the gated feature for the two Pro categories). */}
      <section data-testid="dashboard-category-hub" aria-labelledby="hub-heading">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-flag-red" />
          <h2 id="hub-heading" className="text-xs font-bold uppercase tracking-[0.18em] text-slate-700">Where do you want to work?</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-category-grid">
          <CategoryCard
            to="/advisor"
            testid="cat-advisor"
            icon={Bot}
            title="Advisor"
            blurb="Get AI strategy advice for your race — grounded in your saved profile."
            tone="red"
          />
          <CategoryCard
            to="/communications"
            testid="cat-communications"
            icon={Megaphone}
            title="Communications"
            blurb="Social posts, speeches, and the scheduler — all in one place."
            tone="navy"
          />
          <CategoryCard
            to="/field-data"
            testid="cat-field-data"
            icon={Database}
            title="Field Data"
            blurb="Lawn sign tracker and the volunteer database for your ground game."
            tone="navy"
          />
          <CategoryCard
            to="/planning"
            testid="cat-planning"
            icon={ClipboardList}
            title="Planning"
            blurb="Campaign calendar and your launch checklist — every step in order."
            tone="navy"
          />
          <CategoryCard
            to="/voters"
            testid="cat-voters"
            icon={Users}
            title="Voter List"
            blurb="Upload and manage your voter file — tag, filter, and pull walk lists."
            tone="red"
            pro
          />
          <CategoryCard
            to="/finances"
            testid="cat-finances"
            icon={Wallet}
            title="Finances"
            blurb="Fundraising, budget, expenses, and the report builder for filing periods."
            tone="navy"
            pro
          />
        </div>
      </section>

      {/* SNAPSHOT */}
      <section data-testid="dashboard-snapshot-row">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-700">Today&apos;s snapshot</h2>
        </div>
        <div className={`grid gap-4 ${isCommittee ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
          {!isCommittee && <SnapshotChecklistCard data={checklist} />}
          <SnapshotEventCard event={nextEvent} />
          <SnapshotDraftCard draft={recentDraft} />
        </div>
      </section>

      {/* PROFILE SUMMARY */}
      <ProfileSummary campaign={campaign} isCommittee={isCommittee} />
    </div>
  );
}

/**
 * Large, tap-friendly rectangle. One per category on the new home grid.
 * Mobile + desktop: full bleed, big title, one-line description, optional
 * "Pro" badge in the top-right corner. Click always works — Pro paywall
 * is enforced downstream by ProtectedRoute (so the user lands on the
 * Paywall interstitial which already has the upgrade CTA).
 */
function CategoryCard({ to, testid, icon: Icon, title, blurb, tone = "navy", pro = false }) {
  const iconCls = tone === "red" ? "bg-flag-red text-white" : "bg-flag-navy text-white";
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group relative bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm hover:border-flag-navy hover:shadow-md transition-all flex flex-col min-h-[170px]"
    >
      {pro && (
        <span
          data-testid={`${testid}-pro-badge`}
          className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-flag-red text-white rounded-full px-2 py-0.5 shadow-sm"
        >
          <Lock className="h-2.5 w-2.5" /> Pro
        </span>
      )}
      <div className={`h-12 w-12 rounded-xl ${iconCls} flex items-center justify-center`}>
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="mt-4 font-display text-xl sm:text-2xl font-bold text-flag-navy leading-tight">{title}</h3>
      <p className="mt-1.5 text-slate-600 text-[15px] leading-relaxed flex-1">{blurb}</p>
      <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-flag-red group-hover:gap-2 transition-all">
        Open <ArrowRight className="h-4 w-4" />
      </div>
    </Link>
  );
}
